import { useState, useEffect, useCallback } from 'react';
import api from '../api';

const TYPES = [
  { key: 'billiard', label: '🎱 Bilyard', color: '#10b981' },
  { key: 'playstation', label: '🎮 PlayStation', color: '#8b5cf6' },
];

const RATE_TYPES = [
  { key: 'hourly', label: '⏱ Soatlik (ochiq hisoblagich)' },
  { key: 'package', label: '📦 Paket (fixed narx)' },
];

const S = {
  page: { padding: 28, maxWidth: 900, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 800, color: 'var(--text)' },
  sub: { fontSize: 13, color: 'var(--text2)', marginTop: 4 },
  card: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 12 },
  row: { display: 'flex', gap: 10, alignItems: 'center' },
  badge: (color) => ({ fontSize: 10, padding: '2px 8px', borderRadius: 6, background: color + '22', color, border: `1px solid ${color}44`, fontWeight: 700 }),
  input: { background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  select: { background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  btn: (c) => ({ padding: '8px 16px', borderRadius: 8, border: `1px solid ${c}`, background: c + '18', color: c, cursor: 'pointer', fontSize: 12, fontWeight: 700 }),
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 18, padding: 28, width: 420 },
  label: { display: 'block', fontSize: 11, color: 'var(--text2)', letterSpacing: 1, marginBottom: 5, marginTop: 12 },
};

const EMPTY = {
  station_type: 'billiard', name: '', rate_type: 'hourly',
  hourly_rate: '', package_minutes: '', package_price: '',
  from_hour: 0, to_hour: 24, per_player_extra: 0, active: true,
};

export default function StationRates() {
  const [labs, setLabs] = useState([]);
  const [labId, setLabId] = useState('');
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    api.get('/owner/labs').then(r => {
      setLabs(r.data);
      const saved = localStorage.getItem('selected_lab_id');
      setLabId(saved || (r.data[0]?.id ? String(r.data[0].id) : ''));
    });
  }, []);

  const load = useCallback(() => {
    if (!labId) return;
    setLoading(true);
    api.get(`/owner/station-rates?lab_id=${labId}`)
      .then(r => { setRates(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [labId]);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setForm({ ...EMPTY, station_type: filterType !== 'all' ? filterType : 'billiard' });
    setEditId(null);
    setShowModal(true);
  }

  function openEdit(r) {
    setForm({
      station_type: r.station_type, name: r.name, rate_type: r.rate_type,
      hourly_rate: r.hourly_rate || '', package_minutes: r.package_minutes || '',
      package_price: r.package_price || '', from_hour: r.from_hour ?? 0,
      to_hour: r.to_hour ?? 24, per_player_extra: r.per_player_extra || 0, active: r.active,
    });
    setEditId(r.id);
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.name || !form.rate_type) return;
    setSaving(true);
    try {
      const body = { ...form, lab_id: labId };
      if (editId) await api.patch(`/owner/station-rates/${editId}`, body);
      else await api.post('/owner/station-rates', body);
      setShowModal(false);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    } finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("O'chirilsinmi?")) return;
    await api.delete(`/owner/station-rates/${id}`).catch(() => {});
    load();
  }

  async function toggleActive(r) {
    await api.patch(`/owner/station-rates/${r.id}`, { active: !r.active }).catch(() => {});
    load();
  }

  const filtered = filterType === 'all' ? rates : rates.filter(r => r.station_type === filterType);
  const typeMeta = (t) => TYPES.find(x => x.key === t) || { label: t, color: '#888' };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Stanciya tariflari</div>
          <div style={S.sub}>Bilyard va PlayStation uchun soatlik va paket narxlar</div>
        </div>
        <button style={S.btn('#00d4ff')} onClick={openNew}>+ Yangi tarif</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {[['all', '🏢 Hammasi'], ...TYPES.map(t => [t.key, t.label])].map(([key, label]) => (
          <button key={key} onClick={() => setFilterType(key)} style={{
            padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer',
            fontSize: 12, fontWeight: 600,
            background: filterType === key ? 'rgba(0,212,255,0.15)' : 'transparent',
            color: filterType === key ? 'var(--cyan)' : 'var(--text3)',
          }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--cyan)' }}>Yuklanmoqda...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
          Tarif yo'q — + Yangi tarif bosing
        </div>
      ) : filtered.map(r => {
        const tm = typeMeta(r.station_type);
        return (
          <div key={r.id} style={{ ...S.card, opacity: r.active ? 1 : 0.5 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={S.badge(tm.color)}>{tm.label}</span>
                <span style={S.badge(r.rate_type === 'package' ? '#f59e0b' : '#6366f1')}>
                  {r.rate_type === 'package' ? 'PAKET' : 'SOATLIK'}
                </span>
                {!r.active && <span style={S.badge('#ef4444')}>FAOL EMAS</span>}
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{r.name}</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={S.btn(r.active ? '#f59e0b' : '#22c55e')} onClick={() => toggleActive(r)}>
                  {r.active ? 'O\'chirish' : 'Yoqish'}
                </button>
                <button style={S.btn('#00d4ff')} onClick={() => openEdit(r)}>✏️</button>
                <button style={S.btn('#ef4444')} onClick={() => handleDelete(r.id)}>🗑</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, color: 'var(--text2)' }}>
              {r.rate_type === 'hourly' && r.hourly_rate && (
                <span>💰 <b style={{ color: 'var(--text)' }}>{Number(r.hourly_rate).toLocaleString()} so'm</b>/soat</span>
              )}
              {r.rate_type === 'package' && (
                <>
                  <span>⏱ <b style={{ color: 'var(--text)' }}>{r.package_minutes} daq</b></span>
                  <span>💰 <b style={{ color: 'var(--text)' }}>{Number(r.package_price).toLocaleString()} so'm</b></span>
                </>
              )}
              {r.per_player_extra > 0 && (
                <span>👥 +{Number(r.per_player_extra).toLocaleString()} so'm/qo'shimcha kishi/soat</span>
              )}
              {(r.from_hour > 0 || r.to_hour < 24) && (
                <span>🕐 {r.from_hour}:00 – {r.to_hour}:00</span>
              )}
            </div>
          </div>
        );
      })}

      {/* Modal */}
      {showModal && (
        <div style={S.overlay} onClick={() => setShowModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 18 }}>
              {editId ? 'Tarifni tahrirlash' : 'Yangi tarif'}
            </div>

            <label style={S.label}>STANCIYA TURI</label>
            <select style={{ ...S.select, width: '100%' }} value={form.station_type}
              onChange={e => setForm(f => ({ ...f, station_type: e.target.value }))}>
              {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>

            <label style={S.label}>TARIF NOMI</label>
            <input style={{ ...S.input, width: '100%' }} placeholder="mas: Kechki soatlik"
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />

            <label style={S.label}>TARIF TURI</label>
            <select style={{ ...S.select, width: '100%' }} value={form.rate_type}
              onChange={e => setForm(f => ({ ...f, rate_type: e.target.value }))}>
              {RATE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>

            {form.rate_type === 'hourly' && (
              <>
                <label style={S.label}>SOATLIK NARX (so'm)</label>
                <input style={{ ...S.input, width: '100%' }} type="number" placeholder="15000"
                  value={form.hourly_rate} onChange={e => setForm(f => ({ ...f, hourly_rate: e.target.value }))} />
                {form.station_type === 'billiard' && (
                  <>
                    <label style={S.label}>QO'SHIMCHA KISHI/SOAT (+so'm, ixtiyoriy)</label>
                    <input style={{ ...S.input, width: '100%' }} type="number" placeholder="0"
                      value={form.per_player_extra} onChange={e => setForm(f => ({ ...f, per_player_extra: e.target.value }))} />
                  </>
                )}
              </>
            )}

            {form.rate_type === 'package' && (
              <>
                <label style={S.label}>PAKET DAVOMIYLIGI (daqiqa)</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  {[30, 60, 90, 120].map(m => (
                    <button key={m} onClick={() => setForm(f => ({ ...f, package_minutes: m }))} style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                      background: Number(form.package_minutes) === m ? 'rgba(139,92,246,0.2)' : 'var(--card2)',
                      border: `1px solid ${Number(form.package_minutes) === m ? '#8b5cf6' : 'var(--border)'}`,
                      color: Number(form.package_minutes) === m ? '#8b5cf6' : 'var(--text2)',
                    }}>{m} daq</button>
                  ))}
                </div>
                <input style={{ ...S.input, width: '100%' }} type="number" placeholder="60"
                  value={form.package_minutes} onChange={e => setForm(f => ({ ...f, package_minutes: e.target.value }))} />
                <label style={S.label}>PAKET NARXI (so'm)</label>
                <input style={{ ...S.input, width: '100%' }} type="number" placeholder="20000"
                  value={form.package_price} onChange={e => setForm(f => ({ ...f, package_price: e.target.value }))} />
              </>
            )}

            <label style={S.label}>VAQT ORALIG'I (ixtiyoriy)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>DAN (soat)</div>
                <input style={{ ...S.input, width: '100%' }} type="number" min="0" max="23"
                  value={form.from_hour} onChange={e => setForm(f => ({ ...f, from_hour: Number(e.target.value) }))} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>GACHA (soat)</div>
                <input style={{ ...S.input, width: '100%' }} type="number" min="1" max="24"
                  value={form.to_hour} onChange={e => setForm(f => ({ ...f, to_hour: Number(e.target.value) }))} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button style={{ ...S.btn('#888'), flex: 1 }} onClick={() => setShowModal(false)}>Bekor</button>
              <button style={{ ...S.btn('#00d4ff'), flex: 2 }} onClick={handleSave} disabled={saving}>
                {saving ? '...' : editId ? 'Saqlash' : 'Qo\'shish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
